import { Flex, Spinner, Text, Button } from "@radix-ui/themes";
import { useEffect, useRef, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";

import { logout, selectIsAuthenticated } from "@/entities/session";
import { useAppDispatch, useAppSelector } from "./appStore";
import { resetState } from "@/entities/annotation";
import {
  insightsApi,
  streamsApi,
  useLazyStaffStoreAppDetailsQuery,
} from "@/shared/api";
import { lodApi } from "@/shared/api/lod-api";
import { convertServicesToEnvironment } from "@/shared/lib/environment";
import {
  getInitializedStoreKey,
  refreshAccessToken,
  setInitializedStoreKey,
  updateGlobalAPIBaseURLs,
} from "@/shared/api/axios";
import { useAuthRedirect } from "@/shared/hooks/useAuthRedirect";
import { getAppKey } from "@/shared/api/helpers";
import { ExitIcon } from "@radix-ui/react-icons";
import { appSession } from "@/shared/lib/storage";
import { useUsertour } from "@/shared/hooks/useUsertour";
import { usePostHogIdentify } from "@/shared/hooks/usePostHogIdentify";

export function ProtectedView() {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [getAppDetails] = useLazyStaffStoreAppDetailsQuery();
  const { redirectToLogin } = useAuthRedirect();
  const { storeKey = "" } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const lastStoreKeyRef = useRef<string | null>(null);

  useUsertour();
  usePostHogIdentify();

  // Prevent whole page zooming when Control key is pressed
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey) {
        e.preventDefault();
      }
    };

    window.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      window.removeEventListener("wheel", handleWheel);
    };
  }, []);

  const initializeEnvironment = async ({ storeKey }: { storeKey: string }) => {
    setIsLoading(true);

    try {
      const appDetails = await getAppDetails({
        storeKey,
        appKey: getAppKey(),
      }).unwrap();

      await refreshAccessToken(storeKey);
      const services = convertServicesToEnvironment(
        appDetails?.result?.services
      );

      updateGlobalAPIBaseURLs(services);
      setInitializedStoreKey(storeKey);
    } catch (error) {
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      redirectToLogin();
      setIsLoading(false);
    } else {
      if (storeKey) {
        if (lastStoreKeyRef.current && lastStoreKeyRef.current !== storeKey) {
          dispatch(lodApi.util.resetApiState());
          dispatch(insightsApi.util.resetApiState());
          dispatch(streamsApi.util.resetApiState());
          dispatch(resetState());
        }
        lastStoreKeyRef.current = storeKey;

        if (getInitializedStoreKey() === storeKey) {
          setIsLoading(false);
        } else {
          initializeEnvironment({ storeKey });
        }
      } else {
        navigate("/stores");
        setIsLoading(false);
      }
    }
  }, [isAuthenticated, storeKey]);

  return (
    <>
      {isLoading ? (
        <Flex className="h-dvh" gap="2" justify="center" align="center">
          <Spinner size="3" />
          <Text color="gray" size="2">
            Starting app...
          </Text>
        </Flex>
      ) : error ? (
        <ErrorView error={error} />
      ) : (
        <Outlet />
      )}
    </>
  );
}

function ErrorView({ error }: { error: any }) {
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const { redirectToLoginAfterLogout } = useAuthRedirect();

  const handleLogout = () => {
    dispatch(logout())
      .unwrap()
      .then(() => {
        setLoading(false);
        appSession.removeItem();
        redirectToLoginAfterLogout();
      })
      .catch(() => {
        setLoading(false);
      });
  };

  return (
    <Flex className="h-dvh" gap="1" justify="center" align="center">
      {error?.status && (
        <Text color="gray" size="2">
          {error.status}
        </Text>
      )}
      <Text color="gray" size="2">
        {error?.data?.detail || "Unexpected error. Please try again."}
      </Text>

      <Button
        disabled={loading}
        className="absolute top-6 right-8"
        variant="ghost"
        color="gray"
        onClick={handleLogout}
      >
        <ExitIcon /> Logout
      </Button>
    </Flex>
  );
}
